import { useQuery } from "@tanstack/react-query";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';

export interface CourseProduct {
  product_id: number;
  name: string;
  description: string | null;
  price: number;
  duration: string | null;
  main_photo_url: string | null;
  hasAvailableTrips: boolean;
}

export const useCourseProducts = (courseId: string) => {
  const { supabase } = useSupabaseConnection();
  return useQuery({
    queryKey: ["course-products", courseId],
    queryFn: async (): Promise<CourseProduct[]> => {
      if (!courseId) return [];

      console.log('🔍 코스 상품 조회 시작 - 최적화된 단일 쿼리', {
        courseId
      });

      try {
        // 최적화된 단일 쿼리 사용
        const { data, error } = await supabase
          .rpc('get_course_products_optimized', { p_course_id: courseId });

        if (error) {
          console.error('❌ 코스 상품 조회 실패:', error);
          
          
          throw new Error(`Failed to fetch course products: ${error.message}`);
        }

        if (!data || data.length === 0) {
          console.log('ℹ️ 해당 코스에 등록된 상품이 없습니다');
          return [];
        }

        console.log(`✅ 코스 상품 ${data.length}개 조회 완료`);

        // 타입 변환 (has_available_trips -> hasAvailableTrips)
        return data.map(product => ({
          product_id: product.product_id,
          name: product.name,
          description: product.description,
          price: Number(product.price),
          duration: product.duration,
          main_photo_url: product.main_photo_url,
          hasAvailableTrips: product.has_available_trips
        }));
      } catch (error) {
        console.error('❌ 코스 상품 조회 중 오류 발생:', error);
        
        // 타임아웃이나 네트워크 오류 시 빈 배열 반환
        if (error instanceof Error && error.message.includes('timeout')) {
          console.warn('⏱️ 쿼리 타임아웃 - 빈 배열 반환');
          return [];
        }
        throw error;
      }
    },
    enabled: !!courseId,
    staleTime: 5 * 60 * 1000, // 5분간 캐시
    retry: (failureCount, error) => {
      // 연결 오류인 경우 재시도
      if (error && error.message && (error.message.includes('network') || error.message.includes('fetch'))) {
        return failureCount < 3;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 지수 백오프
  });
};